import {
  FacebookAuthProvider,
  getAuth,
  inMemoryPersistence,
  setPersistence,
  signInWithPopup,
  signOut,
} from 'firebase/auth';
import { getApp, getApps, initializeApp } from 'firebase/app';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../../firebase';
import { FACEBOOK_PAGE_CONFIG_ID } from './facebookLoginConfig';

export const FACEBOOK_PAGE_SCOPES = [
  'pages_show_list',
  'pages_read_engagement',
  'pages_manage_posts',
];

const FACEBOOK_PAGE_AUTH_APP_NAME = 'facebook-page-access';

const createFacebookPageProvider = () => {
  const provider = new FacebookAuthProvider();
  FACEBOOK_PAGE_SCOPES.forEach((scope) => provider.addScope(scope));
  provider.setCustomParameters({
    auth_type: 'rerequest',
    config_id: FACEBOOK_PAGE_CONFIG_ID,
  });
  return provider;
};

const getFacebookPageAuth = async () => {
  const existingApp = getApps().find((app) => app.name === FACEBOOK_PAGE_AUTH_APP_NAME);
  const pageAuthApp = existingApp || initializeApp(
    getApp().options,
    FACEBOOK_PAGE_AUTH_APP_NAME,
  );
  const pageAuth = getAuth(pageAuthApp);

  // This temporary auth session is used only to complete Facebook's consent
  // popup and retrieve a user access token. It must never replace or persist
  // over the customer's existing TingVision login session.
  await setPersistence(pageAuth, inMemoryPersistence);
  return pageAuth;
};

const getFacebookCredential = async () => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('You must be logged in to connect a Facebook Page.');
  }

  const provider = createFacebookPageProvider();
  const pageAuth = await getFacebookPageAuth();
  let credential;
  try {
    const result = await signInWithPopup(pageAuth, provider);
    credential = FacebookAuthProvider.credentialFromResult(result);
  } catch (error) {
    // If the Facebook email already belongs to an email/password Firebase
    // account, Firebase still returns the approved Facebook credential on the
    // error. We only need its access token for the Page API connection.
    credential = FacebookAuthProvider.credentialFromError(error);
    if (!credential?.accessToken) {
      throw error;
    }
  } finally {
    await signOut(pageAuth).catch(() => {});
  }

  const accessToken = credential?.accessToken;
  if (!accessToken) {
    throw new Error('Facebook did not return permission to access your Pages.');
  }

  return accessToken;
};

export const loadFacebookPageConnection = async () => {
  const callable = httpsCallable(functions, 'getFacebookPageConnection');
  const result = await callable();
  return result.data;
};

export const connectFacebookPages = async () => {
  const accessToken = await getFacebookCredential();
  const callable = httpsCallable(functions, 'connectFacebookPages');
  const result = await callable({ accessToken });
  return result.data;
};

export const selectFacebookPage = async (pageId) => {
  const callable = httpsCallable(functions, 'selectFacebookPage');
  const result = await callable({ pageId });
  return result.data;
};

export const disconnectFacebookPages = async () => {
  const callable = httpsCallable(functions, 'disconnectFacebookPages');
  const result = await callable();
  return result.data;
};

export const getFacebookConnectionErrorMessage = (error) => {
  const code = String(error?.code || '');

  if (code.includes('popup-closed-by-user')) {
    return 'The Facebook window was closed before the Page was connected.';
  }
  if (code.includes('popup-blocked')) {
    return 'Your browser blocked the Facebook window. Allow pop-ups and try again.';
  }
  if (code.includes('credential-already-in-use')) {
    return 'That Facebook account is linked to a different TingVision account. Sign in to that account or contact support.';
  }
  if (code.includes('cancelled-popup-request')) {
    return 'Another Facebook connection window is already open.';
  }
  if (code.includes('functions/not-found')) {
    return 'The Facebook connection service is not available yet. Please try again shortly.';
  }
  if (code.includes('functions/internal')) {
    return 'TingVision could not complete the Facebook connection on the server. Please try again.';
  }

  const message = String(error?.message || '')
    .replace(/^Firebase:\s*/i, '')
    .replace(/^FirebaseError:\s*/i, '')
    .trim();
  if (message.toLowerCase() === 'internal') {
    return 'TingVision could not complete the Facebook connection on the server. Please try again.';
  }
  return message || 'TingVision could not connect to Facebook. Please try again.';
};
